import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { logger } from '../utils/logger';
import { chatStorageService } from '../services';

/**
 * Custom hook to manage WebSocket chat functionality
 * This hook provides a clean interface for components to interact with WebSocket
 * @param {Object} webSocketService - Service that handles WebSocket connections
 * @param {Object} chatApi - Service that handles REST API chat functionality
 * @param {string} welcomeMessage - Optional welcome message to display initially
 * @returns {Object} WebSocket chat state and functionality
 */
const useWebSocketChat = (
  webSocketService, 
  chatApi, 
  welcomeMessage = "¡Hola! Soy el Agente Ualabi, tu asistente especialista en documentación técnica. ¿En qué puedo ayudarte hoy?"
) => {
  const { currentUser, isAuthenticated, loading: authLoading } = useAuth();
  
  // Load messages from localStorage or initialize with welcome message
  const [messages, setMessages] = useState(() => {
    const savedMessages = chatStorageService.loadMessages();
    if (savedMessages && savedMessages.length > 0) {
      logger.log('Loaded chat history from localStorage:', savedMessages.length, 'messages');
      return savedMessages;
    }
    return [{ id: 1, text: welcomeMessage, sender: "bot" }];
  });
  
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Generate or retrieve user identifier
  // Only authenticated users will get an identifier
  const getUserIdentifier = useCallback(() => {
    if (currentUser && currentUser.id) {
      logger.log('Authenticated user ID:', currentUser.id);
      return currentUser.id;
    }
    logger.log('Unauthenticated user, no ID assigned');
    return null;
  }, [currentUser]);

  // Connect to WebSocket only after authentication is complete
  useEffect(() => {
    // Skip initialization until auth is ready
    if (authLoading) {
      logger.log('Auth is still loading, waiting before WebSocket initialization');
      return () => {};
    }
    
    setIsInitialized(true);
  }, [authLoading]);

  // Handle WebSocket connection only after auth is initialized
  useEffect(() => {
    // Only proceed if we've finished checking authentication
    if (!isInitialized) {
      return () => {};
    }

    // Only attempt to connect if user is authenticated
    if (!isAuthenticated) {
      logger.log('User is not authenticated, showing login message');
      setIsConnected(false);
    }
    
    const userId = getUserIdentifier();
    const token = localStorage.getItem('token');
    
    if (userId && token) {
      logger.log(`Attempting connection with userId: ${userId}, token present: ${!!token}`);
      webSocketService.connect(userId, token);
      
      // Handle incoming messages
      const messageHandler = (data) => {
        logger.log('Received message:', data);
        
        if (data.type === 'connection_status' && data.status === 'connected') {
          setIsConnected(true);
          setError(null);
        } else if (data.type === 'auth_required') {
          // Handle authentication required message
          setIsConnected(false);
        } else if (data.type === 'message') {
          // Handle regular message
          setMessages(prev => {
            const newBotMessage = {
              id: prev.filter(m => !m.isTemporary).length + 1,
              text: data.content || data.text,
              sender: data.sender === 'user' ? 'user' : 'bot'
            };
            // Remove any temporary processing messages
            return [
              ...prev.filter(m => !m.isTemporary),
              newBotMessage
            ];
          });
          setIsSending(false);
        } else if (data.type === 'error') {
          // Handle error messages
          setError(data.message || 'Se produjo un error en la comunicación');
          setIsSending(false);
        }
      };
      
      // Handle connection status changes
      const connectionHandler = (status) => {
        setIsConnected(status);
        if (!status) {
          setError('Conexión perdida. Intentando reconectar...');
        } else {
          setError(null);
        }
      };
      
      // Register handlers
      const removeMessageHandler = webSocketService.onMessage(messageHandler);
      const removeConnectionHandler = webSocketService.onConnectionStatus(connectionHandler);
      
      // Cleanup function
      return () => {
        removeMessageHandler();
        removeConnectionHandler();
        webSocketService.closeConnection();
      };
    } else {
      logger.log('User not authenticated or missing token, skipping WebSocket connection');
      setIsConnected(false);
      
      // Cleanup function
      return () => {};
    }
  }, [webSocketService, getUserIdentifier, isAuthenticated, currentUser, isInitialized]);

  // Handle authentication changes without reconnecting
  useEffect(() => {
    // When authentication status changes, we don't need to reconnect the WebSocket
    // But we may want to update message handling behavior
    if (isAuthenticated && currentUser) {
      logger.log('User authenticated:', currentUser.id);
      // Any authentication-dependent behavior can go here
    } else {
      logger.log('User not authenticated or no user data');
    }
  }, [isAuthenticated, currentUser]);

  // Save messages to localStorage when they change
  useEffect(() => {
    if (messages && messages.length > 0) {
      logger.log('Saving messages to localStorage:', messages.length, 'messages');
      chatStorageService.saveMessages(messages);
    }
  }, [messages]);

  // Send a message
  const sendMessage = async (text) => {
    if (!text.trim()) return;
    
    // Block message sending for unauthenticated users
    if (!currentUser) {
      setError('Para enviar mensajes es necesario iniciar sesión');
      return;
    }
    
    const userId = getUserIdentifier();
    
    // Create message object
    const messageData = {
      user_id: userId,
      content: text,
      timestamp: new Date().toISOString(),
      channel: 'webchat'
    };
    
    // Add user information
    messageData.user_email = currentUser.email;
    messageData.is_authenticated = true;
    
    // Add user message to UI immediately
    const newUserMessage = {
      id: messages.filter(m => !m.isTemporary).length + 1,
      text: text,
      sender: "user"
    };
    // Add processing info bubble and disable input
    const processingMessage = {
      id: newUserMessage.id + 1,
      text: "Procesando tu mensaje...",
      sender: "bot",
      isTemporary: true
    };
    setIsSending(true);
    setMessages(prev => [...prev, newUserMessage, processingMessage]);
    
    try {
      // First try WebSocket if connected
      if (isConnected) {
        webSocketService.sendMessage(messageData);
      } else {
        // Fallback to REST API if WebSocket is not connected
        const response = await chatApi.sendMessage(messageData);
        
        // Handle auth required response from REST API
        if (response && response.status === 'auth_required') {
          setMessages(prevMessages => {
            // Remove temporary processing message
            const messagesWithoutProcessing = prevMessages.filter(msg => !msg.isTemporary);
            
            // Add the auth required message
            return [
              ...messagesWithoutProcessing,
              {
                id: messagesWithoutProcessing.length + 1,
                text: response.content,
                sender: 'bot',
                isSystem: true,
                loginUrl: response.login_url
              }
            ];
          });
        }
      }
    } catch (error) {
      logger.error('Failed to send message:', error);
      setError('Error al enviar el mensaje. Por favor, intenta nuevamente.');
      setIsSending(false);
    }
  };

  // Clear error message
  const clearError = () => setError(null);

  return {
    messages,
    isConnected,
    error,
    sendMessage,
    clearError,
    isAuthLoading: authLoading,
    isSending
  };
}

export default useWebSocketChat;
